import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { inject } from "@vercel/analytics";
import { injectSpeedInsights } from "@vercel/speed-insights";

import App from "./App.tsx";
import { ErrorFallback } from "./ErrorFallback.tsx";
import { AuthProvider } from "./contexts/AuthContext.tsx";

import "@fontsource-variable/inter";
import "@fontsource/jetbrains-mono/400.css";
import "@fontsource/jetbrains-mono/500.css";
import "./main.css";

if (import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: [Sentry.browserTracingIntegration()],
    // Capture 10% of transactions in production; 100% in dev/preview
    tracesSampleRate: import.meta.env.PROD ? 0.1 : 1,
    // Only send errors from our own origin
    tracePropagationTargets: [/^\//, /127\.0\.0\.1/, /coleccionista/],
    beforeSend(event) {
      // Ignore errors from Instagram's in-app browser injected scripts.
      // These originate from iabjs://navigation_performance_logger_android and
      // are caused by Instagram's own Java bridge being garbage-collected —
      // not actionable from our side.
      const frames = event.exception?.values?.[0]?.stacktrace?.frames ?? [];
      const fromInstagramIAB = frames.some((f) =>
        f.filename?.startsWith("iabjs://"),
      );
      if (fromInstagramIAB) return null;

      // Ignore chunk load errors caused by stale cache after a new deploy.
      // ErrorFallback already handles these by reloading the page once —
      // nothing to act on from our side.
      const errorMessage = event.exception?.values?.[0]?.value ?? "";
      const isChunkError =
        errorMessage.includes("is not a valid JavaScript MIME type") ||
        errorMessage.includes("Failed to fetch dynamically imported module") ||
        errorMessage.includes("Loading chunk") ||
        event.exception?.values?.[0]?.type === "ChunkLoadError";
      if (isChunkError) return null;

      return event;
    },
  });
}

inject();
injectSpeedInsights();

createRoot(document.getElementById("root")!).render(
  <Sentry.ErrorBoundary fallback={ErrorFallback}>
    <AuthProvider>
      <App />
    </AuthProvider>
  </Sentry.ErrorBoundary>,
);
